import cx from 'classnames'
import Styles from './Entry.module.scss'
import TemplatePreviewStyles from '../TemplatePreview/TemplatePreview.module.scss'
import dayjs, { Dayjs } from 'dayjs'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { ChevronDown, Scheduled } from 'blaze-ui/icons'
import { Calendar } from 'components/Calendar/Calendar'
import { TimePicker } from 'components/DateTimePicker/TimePicker'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { Button } from 'blaze-ui/Button'
import { Pressable } from '@react-aria/interactions'
import { Text } from 'blaze-ui/Text/Text'
import { Blog } from 'almanac-ui/icons/blaze/32'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { AddMedia } from '../../components/AddMedia/AddMedia'
import { MediaPreview } from '../../components/AddMedia/MediaPreview'
import { EditKey, EditMenu } from '../../components/EditMenu/EditMenu'
import { EditDrawer } from '../../components/EditMenu/EditDrawer'
import { EntryProps } from './Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Key, useRef, useState } from 'react'
import { useOpenImagePicker } from 'components/ImagePickerModal/useOpenImagePicker'
import { ScheduledContent } from '../../ScheduleContent'
import { SelectedImage } from 'types'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import { TemplatesModalV2 } from 'components/Templates/TemplatesModalV2/TemplatesModalV2'
import { DesignChannel } from 'components/NewFileModal/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useContentPlanningContext } from 'components/Wizards/PlanningWizard/ContentPlanningContext'
import { GeneratedDesignPreview } from '../GeneratedDesignPreview/GeneratedDesignPreview'
import { Ref } from 'components/DesignContentPreview/Types'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { getImageShapes } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { hasPlaceholderImage } from 'components/Wizards/DesignWizard/utils'
import { useField } from 'formik'
import {
  ImageMenuSelectedImage,
  Provider as ImageMenuModalProvider,
} from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { Provider as ImagePickerModalProvider } from 'components/ImagePickerModal/Types'
import { GeneratedArchetypePreview } from '../GeneratedArchetypePreview/GeneratedArchetypePreview'

const Entry = observer(
  ({
    content,
    lineClamp,
    isCalendarOpen,
    onCalendarOpenChange,
    isEditingCaption,
    onEditingCaptionChange,
    onUpdateCaption,
    inputPlaceholder,
    index,
    externalIntegrationType,
    setScheduledContent,
    scheduledContent,
    onUpdateDesignContent,
    onGenerate,
  }: EntryProps) => {
    const [helper] = useField<number>('brand_kit_id')
    const { drawersStore, visualElements } = useRootStore()
    const { generationType } = useContentPlanningContext()
    const { openImagePicker, closeImagePicker } = useOpenImagePicker()
    const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
    const workspaceId = useCurrentWorkspaceId()
    const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
    const { openModal, closeStack, closeModal } = useModals()
    const [draftCaption, setDraftCaption] = useState(content.text)
    const { isMobile } = useResponsiveDesign()
    const generatedDesignPreviewRef = useRef<Ref>(null)
    const templateId = content.sourceVisualElementId
    const { archetype, width, height } = content
    const isDesign = Boolean(width && height)

    const handleConfirm = (images: SelectedImage[] | ImageMenuSelectedImage[], content: ScheduledContent) => {
      if (!images[0].id) return

      MediaFileClient.getAlmanacFile(images[0].id).then((mediaFile) => {
        if (!generatedDesignPreviewRef.current) return

        const editor = generatedDesignPreviewRef.current.getEditor()
        const replaceableImage = getImageShapes(editor).find((shape) => {
          if (shape.type === 'image') {
            return !shape.props.isLogo
          }

          if (shape.type === 'mask') {
            return shape.props.isReplaceable || shape.props.wasReplaced
          }

          return false
        })
        if (!replaceableImage) return

        generatedDesignPreviewRef.current
          .replaceMediaFile(replaceableImage.id, mediaFile.data.almanac_file)
          .then((snapshot) => {
            setScheduledContent(
              scheduledContent.map((c) => {
                if (c.uuid === content.uuid) {
                  return { ...c, content: snapshot, initialMediaFileId: images[0].id }
                }

                return c
              }),
            )
          })
      })

      if (isMobile) closeImagePicker()
    }

    const openMediaReplacementModal = (content: ScheduledContent) => {
      const modalProps = {
        maxCount: 1,
        onPressOutside: closeModal,
        hiddenProviders: ['gifs', 'stockVideo', 'hardcodedAi'] as ImageMenuModalProvider[],
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
        initialBrandKitId: helper.value,
        uploadMedia: true,
        onUse: (images: ImageMenuSelectedImage[]) => handleConfirm(images, content),
      }

      const drawerProps = {
        onConfirm: (images: SelectedImage[]) => handleConfirm(images, content),
        uploadImages: async (files: File[]) => {
          const uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId)
          if (!uploadedImages) return []
          return uploadedImages.map(
            (file) => ({ id: file.id, url: file.source_url, mime_type: file.mime_type } as SelectedImage),
          )
        },
        onPressOutside: closeImagePicker,
        onCancel: closeImagePicker,
        defaultProvider: isMobile ? ('uploads' as ImagePickerModalProvider) : undefined,
        maxCount: 1,
        brandKitId: helper.value,
      }

      openImagePicker(drawerProps, modalProps, { newStack: true })
    }

    const openTemplateSelectionModal = async (content: ScheduledContent) => {
      if (!width || !height) {
        return
      }

      openModal(
        TemplatesModalV2,
        {
          onUse: async (templateId) => {
            setScheduledContent(
              scheduledContent.map((c) => {
                const template = visualElements.getElement(templateId)
                const hasReplaceableImage = hasPlaceholderImage(template)
                if (c.uuid === content.uuid)
                  return {
                    ...c,
                    archetype: undefined,
                    sourceVisualElementId: templateId,
                    hasReplaceableImage,
                    initialMediaFileId: hasReplaceableImage ? content.initialMediaFileId : undefined,
                  }
                return c
              }),
            )
            closeStack()
          },
          designChannel: generationType as DesignChannel,
          height,
          width,
          disabledFilters: ['format'],
        },
        { newStack: true },
      )
    }

    const handleAction = async (key: Key, uuid: string) => {
      if (key === EditKey.ChangeAddImage) {
        const scheduledContentToUpdate = scheduledContent.find((c) => c.uuid === uuid)

        if (!scheduledContentToUpdate) {
          return
        }

        openMediaReplacementModal(scheduledContentToUpdate)
      }

      if (key === EditKey.ChangeTemplate) {
        const scheduledContentToUpdate = scheduledContent.find((c) => c.uuid === uuid)

        if (!scheduledContentToUpdate) {
          return
        }

        openTemplateSelectionModal(scheduledContentToUpdate)
      }

      if (key === EditKey.EditCaption) {
        onEditingCaptionChange(true)
      }

      if (key === EditKey.Delete) {
        setScheduledContent(scheduledContent.filter((c) => c.uuid !== uuid))
      }
    }

    const updateDate = (date: Dayjs, uuid: string) => {
      setScheduledContent(
        scheduledContent.map((content) => {
          if (uuid === content.uuid) {
            return { ...content, postAt: content.postAt?.month(date.month()).date(date.date()) }
          }

          return content
        }),
      )
    }

    const updateTime = (newTime: Dayjs, uuid: string) => {
      setScheduledContent(
        scheduledContent.map((content) => {
          if (uuid === content.uuid) {
            const updatedDateTime = content.postAt?.hour(newTime.hour()).minute(newTime.minute())
            return { ...content, postAt: updatedDateTime }
          }

          return content
        }),
      )
    }

    return (
      <div
        data-testid={`content-${content.uuid}`}
        className={cx(Styles.scheduledContentContainer, {
          [Styles.roundedTopCorners]: index === 0 && !isMobile,
          [Styles.borderBottom]: index === scheduledContent.length - 1 && !isMobile,
        })}
      >
        <div
          className={cx(Styles.iconAndText, {
            [Styles.withoutTemplateAndMedia]: !isDesign,
          })}
        >
          {templateId && (
            <div className={Styles.designPreviewContainer}>
              <GeneratedDesignPreview
                ref={generatedDesignPreviewRef}
                templateId={templateId}
                initialMediaFileId={content.initialMediaFileId}
                onGenerate={onGenerate}
                onChange={(updatedContent) => {
                  onUpdateDesignContent?.(updatedContent)
                }}
              />
            </div>
          )}
          {archetype && width && height && (
            <div className={Styles.designPreviewContainer}>
              <GeneratedArchetypePreview
                ref={generatedDesignPreviewRef}
                archetype={archetype}
                width={width}
                height={height}
                onGenerate={onGenerate}
                initialMediaFileId={content.initialMediaFileId}
                onChange={(updatedContent) => {
                  onUpdateDesignContent?.(updatedContent)
                }}
              />
            </div>
          )}
          {isDesign && !content.initialMediaFileId && (
            <AddMedia
              hasReplaceableImage={content.hasReplaceableImage ?? false}
              onPress={() => openMediaReplacementModal(content)}
            />
          )}
          {isDesign && content.initialMediaFileId && (
            <MediaPreview
              fileId={content.initialMediaFileId}
              onPressChange={() => openMediaReplacementModal(content)}
            />
          )}
          {content.previewUrl && (
            <div className={TemplatePreviewStyles.preview}>
              <img src={content.previewUrl} alt="" />
            </div>
          )}
          <ResponsiveDesign.Desktop>
            {!isDesign && externalIntegrationType && (
              <div className={Styles.icon}>
                {externalIntegrationType === ExternalIntegrationEnum.WordPress ? (
                  <Blog size={20} />
                ) : (
                  <PlatformIcon platform={externalIntegrationType} size={20} />
                )}
              </div>
            )}
          </ResponsiveDesign.Desktop>
          <ResponsiveDesign.Mobile>
            {!isEditingCaption && (
              <div className={Styles.editMenuContainer}>
                <Button
                  variant="tertiary"
                  size="md"
                  onPress={() => drawersStore.openDrawer(EditDrawer, { content, onAction: handleAction })}
                >
                  Edit
                </Button>
              </div>
            )}
          </ResponsiveDesign.Mobile>
          <div
            className={cx(Styles.text, {
              [Styles.isEditing]: isEditingCaption,
              [Styles.withoutTemplateAndMedia]: !isDesign,
            })}
          >
            {isEditingCaption && (
              <Input
                data-testid={`content-input-${content.uuid}`}
                id={content.uuid}
                scrollable={false}
                size="sm"
                name={`Content ${content.uuid}`}
                value={draftCaption}
                onChange={(e) => {
                  setDraftCaption(e.target.value)
                }}
                autoFocus
                aria-label="Generated Content"
                placeholder={inputPlaceholder}
                onKeyDown={(e) => {
                  if (e.code === 'Escape') {
                    setDraftCaption(content.text)
                    onEditingCaptionChange(false)
                  }
                  // Prevent up and down from selecting other radio options
                  e.stopPropagation()
                }}
                isTextarea
              />
            )}
            {!isEditingCaption && (
              <span className={Styles.displayText}>
                <Text
                  variant={lineClamp === 2 ? 'largeList' : 'secondary'}
                  lineClamp={lineClamp}
                  color={lineClamp === 2 ? undefined : 'var(--dark-80)'}
                >
                  {content.text}
                </Text>
              </span>
            )}
            <ResponsiveDesign.Desktop>
              {!isEditingCaption && <EditMenu content={content} onAction={handleAction} />}
            </ResponsiveDesign.Desktop>
            {isEditingCaption && (
              <div className={Styles.editingCaptionButtonsContainer}>
                <Button
                  size="sm"
                  variant="primary"
                  onPress={() => {
                    onUpdateCaption(draftCaption)
                  }}
                >
                  Save Changes
                </Button>
                <Button
                  size="sm"
                  variant="tertiary"
                  onPress={() => {
                    setDraftCaption(content.text)
                    onEditingCaptionChange(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
        <ResponsiveDesign.Mobile>
          <span className={Styles.postDateLabel}>Post Date</span>
        </ResponsiveDesign.Mobile>
        <MenuTrigger placement="bottom-start" onOpenChange={onCalendarOpenChange}>
          {isMobile ? (
            <Button variant="tertiary" frontIcon={Scheduled} endIcon={ChevronDown} justifyContent="flex-start">
              <span className={Styles.buttonText}>{content.postAt.format('MMM DD, YYYY [at] h:mm A')}</span>
            </Button>
          ) : (
            <Pressable>
              <div
                className={cx(Styles.dateContainer, {
                  [Styles.calendarOpen]: isCalendarOpen,
                })}
              >
                <span className={Styles.dateText}>{content.postAt.format('MMM DD, YYYY [at] h:mm A')}</span>
                <ChevronDown color="currentColor" />
              </div>
            </Pressable>
          )}
          <div className={Styles.calendarContainer}>
            <Calendar
              value={content.postAt.format('YYYY-MM-DD')}
              onChange={(value) => {
                updateDate(dayjs(value), content.uuid)
              }}
            />
            <div className={Styles.divider} />
            <div className={Styles.timePickerContainer}>
              <TimePicker value={content.postAt} onChange={(newTime) => updateTime(newTime, content.uuid)} isDisabled />
            </div>
          </div>
        </MenuTrigger>
      </div>
    )
  },
)

export { Entry }
